import type { ApiClient, RequestBody } from '@vrplatform/data';
import { day, type Maybe } from '@vrplatform/utils';

type ListingOwnershipUpdateBody = Pick<
  RequestBody<'put:/listings/ownership-periods/{id}'>,
  'endAt' | 'onConflict'
>;

type RecurringFeeUpdateBody = Pick<
  RequestBody<'put:/recurring-fees/listing-periods/{id}'>,
  'endAt' | 'onConflict'
>;

type ListingDeactivationApiClient = Pick<ApiClient, 'DELETE' | 'POST' | 'PUT'>;

type ListingOwnership = {
  id: string;
  startAt: string;
};

type ListingRecurringFeePeriod = {
  id: string;
  startAt?: string | null;
};

export type ListingDeactivationTarget = {
  id: string;
  activeOwnership?: Maybe<ListingOwnership>;
  activeRecurringFeePeriods?: Maybe<ListingRecurringFeePeriod[]>;
};

type OwnershipAction =
  | {
      type: 'delete';
      id: string;
    }
  | {
      type: 'update';
      id: string;
      body: ListingOwnershipUpdateBody;
    };

type RecurringFeeAction =
  | {
      type: 'delete';
      id: string;
    }
  | {
      type: 'update';
      id: string;
      body: RecurringFeeUpdateBody;
    };

export type ListingDeactivationPlan = {
  ownershipAction?: OwnershipAction;
  recurringFeeActions: RecurringFeeAction[];
  deactivationBody: RequestBody<'post:/listings/ownership-periods'>;
};

const shouldDeletePeriodAtStart = (
  periodStartAt: Maybe<string> | undefined,
  deactivationStartAt: string
) =>
  !!periodStartAt && day(periodStartAt).isSame(day(deactivationStartAt), 'day');

export const getListingDeactivationPlan = ({
  listing,
  startAt,
  endAt,
}: {
  listing: ListingDeactivationTarget;
  startAt: string;
  endAt: string | null;
}): ListingDeactivationPlan => {
  const ownershipAction = listing.activeOwnership
    ? shouldDeletePeriodAtStart(listing.activeOwnership.startAt, startAt)
      ? {
          type: 'delete' as const,
          id: listing.activeOwnership.id,
        }
      : {
          type: 'update' as const,
          id: listing.activeOwnership.id,
          body: {
            endAt: startAt,
            onConflict: 'updateExisting' as const,
          },
        }
    : undefined;

  const recurringFeeActions =
    listing.activeRecurringFeePeriods?.map((period) =>
      shouldDeletePeriodAtStart(period.startAt, startAt)
        ? {
            type: 'delete' as const,
            id: period.id,
          }
        : {
            type: 'update' as const,
            id: period.id,
            body: {
              endAt: startAt,
              onConflict: 'updateExisting' as const,
            },
          }
    ) ?? [];

  return {
    ownershipAction,
    recurringFeeActions,
    deactivationBody: {
      listingId: listing.id,
      members: [],
      setListingInactive: true,
      startAt,
      endAt,
      reserve: 0,
      businessModel: null,
      onConflict: 'updateExisting',
    },
  };
};

type ApiMutationResult = {
  error?: unknown;
};

const assertApiSuccess = async (
  request: Promise<ApiMutationResult>
): Promise<void> => {
  const result = await request;

  if (result.error) {
    throw result.error;
  }
};

const runOwnershipAction = async (
  $api: ListingDeactivationApiClient,
  action: OwnershipAction
) => {
  if (action.type === 'delete') {
    await assertApiSuccess(
      $api.DELETE('/listings/ownership-periods/{id}', {
        params: {
          path: {
            id: action.id,
          },
          query: {
            onLocked: 'error',
          },
        },
      })
    );

    return;
  }

  await assertApiSuccess(
    $api.PUT('/listings/ownership-periods/{id}', {
      params: {
        path: {
          id: action.id,
        },
      },
      body: action.body,
    })
  );
};

const runRecurringFeeAction = async (
  $api: ListingDeactivationApiClient,
  action: RecurringFeeAction
) => {
  if (action.type === 'delete') {
    await assertApiSuccess(
      $api.DELETE('/recurring-fees/listing-periods/{id}', {
        params: {
          path: {
            id: action.id,
          },
          query: {
            onLocked: 'error',
          },
        },
      })
    );

    return;
  }

  await assertApiSuccess(
    $api.PUT('/recurring-fees/listing-periods/{id}', {
      params: {
        path: {
          id: action.id,
        },
      },
      body: action.body,
    })
  );
};

export const submitListingDeactivation = async ({
  $api,
  listing,
  startAt,
  endAt,
}: {
  $api: ListingDeactivationApiClient;
  listing: ListingDeactivationTarget;
  startAt: string;
  endAt: string | null;
}) => {
  const plan = getListingDeactivationPlan({
    listing,
    startAt,
    endAt,
  });

  await Promise.all([
    ...(plan.ownershipAction
      ? [runOwnershipAction($api, plan.ownershipAction)]
      : []),
    ...plan.recurringFeeActions.map((action) =>
      runRecurringFeeAction($api, action)
    ),
  ]);

  await assertApiSuccess(
    $api.POST('/listings/ownership-periods', {
      body: plan.deactivationBody,
    })
  );
};
